import {
    Component,
    computed,
    DestroyRef,
    EventEmitter,
    inject,
    Output,
    signal
} from '@angular/core';

import {
    NavigationEnd,
    Router
} from '@angular/router';

import {
    NzIconModule
} from 'ng-zorro-antd/icon';

import {
    NzBadgeModule
} from 'ng-zorro-antd/badge';

import {
    NzAvatarModule
} from 'ng-zorro-antd/avatar';

import {
    filter
} from 'rxjs';

import {
    takeUntilDestroyed
} from '@angular/core/rxjs-interop';

import {
    AuthService
} from '../../core/auth/services/auth.service';

import {
    SessionService
} from '../../core/auth/services/session.service';


@Component({
    selector: 'app-topbar',

    imports: [
        NzIconModule,
        NzBadgeModule,
        NzAvatarModule
    ],

    templateUrl: './topbar.html',

    styleUrl: './topbar.css'
})
export class Topbar {

    /*
     * =========================================================
     * OUTPUT
     * =========================================================
     */

    @Output()
    menuClick =
        new EventEmitter<void>();


    /*
     * =========================================================
     * SERVICES
     * =========================================================
     */

    private readonly authService =
        inject(AuthService);


    private readonly session =
        inject(SessionService);


    private readonly router =
        inject(Router);


    private readonly destroyRef =
        inject(DestroyRef);


    /*
     * =========================================================
     * PAGE
     * =========================================================
     */

    readonly pageTitle =
        signal(
            'RestaurantOS'
        );


    /*
     * =========================================================
     * USER
     * =========================================================
     */

    readonly user =
        this.session.currentUser;


    readonly displayName =
        this.session.displayName;


    readonly primaryRole =
        computed(() => {

            const roles =
                this.session.roles();


            if (
                roles.includes(
                    'PLATFORM_ADMIN'
                )
            ) {

                return 'Administrador de plataforma';
            }


            if (
                roles.includes(
                    'OWNER'
                )
            ) {

                return 'Propietario';
            }


            if (
                roles.includes(
                    'MANAGER'
                )
            ) {

                return 'Gerente';
            }


            if (
                roles.includes(
                    'CASHIER'
                )
            ) {

                return 'Cajero';
            }


            if (
                roles.includes(
                    'KITCHEN'
                )
            ) {

                return 'Cocina';
            }


            if (
                roles.includes(
                    'BAR'
                )
            ) {

                return 'Bar';
            }


            if (
                roles.includes(
                    'STAFF'
                )
            ) {

                return 'Personal';
            }


            if (
                roles.includes(
                    'CUSTOMER'
                )
            ) {

                return 'Cliente';
            }


            return 'Usuario';
        });


    readonly initials =
        computed(() => {

            const name =
                this.displayName()
                    .trim();


            if (!name) {

                return 'U';
            }


            const parts =
                name.split(
                    /\s+/
                );


            if (
                parts.length === 1
            ) {

                return parts[0]
                    .substring(
                        0,
                        2
                    )
                    .toUpperCase();
            }


            return (
                parts[0][0] +
                parts[
                    parts.length - 1
                ][0]
            ).toUpperCase();
        });


    /*
     * =========================================================
     * CONSTRUCTOR
     * =========================================================
     */

    constructor() {

        /*
         * Obtenemos el título correspondiente
         * a la ruta actual apenas se crea
         * el Topbar.
         */
        this.updatePageTitle();


        /*
         * Cada vez que Angular termina
         * una navegación SPA actualizamos
         * el título.
         *
         * No existe refresh.
         */
        this.router.events
            .pipe(

                filter(
                    event =>
                        event instanceof
                        NavigationEnd
                ),

                takeUntilDestroyed(
                    this.destroyRef
                )

            )
            .subscribe(
                () => {

                    this.updatePageTitle();

                }
            );
    }


    /*
     * =========================================================
     * PAGE TITLE
     * =========================================================
     */

    private updatePageTitle(): void {

        /*
         * Empezamos desde la raíz
         * del árbol actual del Router.
         */
        let route =
            this.router
                .routerState
                .snapshot
                .root;


        /*
         * Bajamos hasta encontrar
         * la ruta más profunda.
         *
         * Ejemplo:
         *
         * AppLayout
         *     ↓
         * dining-tables
         */
        while (
            route.firstChild
        ) {

            route =
                route.firstChild;
        }


        const title =
            route.data[
                'pageTitle'
            ];


        this.pageTitle.set(

            typeof title === 'string'

                ? title

                : 'RestaurantOS'

        );
    }


    /*
     * =========================================================
     * LOGOUT
     * =========================================================
     */

    logout(): void {

        this.authService
            .logout();


        void this.router.navigate([
            '/login'
        ]);
    }
}